import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getFavorites, toggleFavorite } from '../../utils/api';

interface FavoritesContextType {
  favoriteIds: Set<number>;
  toggle: (productId: number) => Promise<void>;
  isFavorited: (productId: number) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType>({
  favoriteIds: new Set(),
  toggle: async () => {},
  isFavorited: () => false,
});

export const FavoritesProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      setFavoriteIds(new Set());
      return;
    }
    getFavorites().then(res => {
      if (res.success && res.data) {
        const ids: number[] = (res as any).ids || [];
        setFavoriteIds(new Set(ids));
      }
    });
  }, [isAuthenticated]);

  const toggle = useCallback(async (productId: number) => {
    if (!isAuthenticated) return;
    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
    const res = await toggleFavorite(productId);
    if (!res.success) {
      // Rollback bei Fehler
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (next.has(productId)) next.delete(productId);
        else next.add(productId);
        return next;
      });
    }
  }, [isAuthenticated]);

  const isFavorited = useCallback((productId: number) => favoriteIds.has(productId), [favoriteIds]);

  return (
    <FavoritesContext.Provider value={{ favoriteIds, toggle, isFavorited }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
